<ng-container *ngIf="payment">
  <div class="relative" [class]="static ? 'h-[calc(100vh-4rem)]' : ''">
    <div class="flex flex-col p-4 gap-4">
      <!-- logo and payment id -->
      <div *ngIf="static" class="flex justify-between mb-12">
        <img [src]="logo" class="h-24" />
        <div class="text-right">
          <div class="text-xl text-gray-900">
            {{ payment.amount / 100 | currency }} {{ payment.paymentDate ? '| Paid on' : '| Due on' }}
            {{
              payment.paymentDate || payment.dueDate
                ? (payment.paymentDate || payment.dueDate | date: 'MMM d, yyyy')
                : '-'
            }}
          </div>
          <div class="text-sm text-gray-700">{{ payment.id }}</div>
        </div>
      </div>

      <!-- header (amount and date) -->
      <div *ngIf="!static" class="flex justify-between flex-wrap gap-3">
        <div class="text-2xl text-gray-700">
          {{ payment.amount / 100 | currency }} {{ payment.paymentDate ? '| Paid on' : '| Due on' }}
          {{
            payment.paymentDate || payment.dueDate
              ? (payment.paymentDate || payment.dueDate | date: 'MMM d, yyyy')
              : '-'
          }}
        </div>

        <a
          *ngIf="!static && payment.receiptUrl"
          download
          vs-button
          size="sm"
          target="_blank"
          appearance="outline"
          class="min-w-[32px]"
          [href]="payment.receiptUrl"
        >
          <vs-icon slot="start" name="download-01"></vs-icon>
          Download receipt
        </a>
      </div>

      <vs-divider *ngIf="!static" class="mb-4"></vs-divider>

      <div class="mb-4 flex flex-wrap justify-between gap-8">
        <!-- Bill from -->
        <div class="flex flex-col gap-2 flex-1">
          <div class="text-xs font-semibold text-gray-900">Bill from</div>
          <div class="flex flex-col">
            <p class="text-sm font-semibold text-gray-900">
              {{ brandName }}
            </p>
            <p class="text-xs font-normal text-gray-700">
              <a class="hover:text-gray-500" href="mailto:{{ contactEmail }}">
                {{ contactEmail }}
              </a>
            </p>
            <p *ngIf="contactPhone" class="text-xs font-normal text-gray-700">
              <a class="hover:text-gray-500" href="tel:{{ contactPhone }}">
                {{ contactPhone }}
              </a>
            </p>
            <p *ngIf="contactAddress as address" class="text-xs font-normal text-gray-700 whitespace-normal">
              {{ address.line1 }}<ng-container *ngIf="address.line2">, {{ address.line2 }} </ng-container><br />
              {{ address.city }} {{ address.state }} {{ address.zip }}<br />
              {{ address.country }}
            </p>
          </div>
        </div>

        <div class="flex flex-wrap gap-8 flex-1">
          <!-- Bill to -->
          <div class="flex flex-col gap-2 flex-1">
            <div class="text-xs font-semibold text-gray-900">Bill to</div>
            <div class="flex flex-col">
              <p class="text-sm font-semibold text-gray-900">
                {{ payment.user.displayName ? payment.user.displayName : payment.user.email }}
              </p>
              <a class="text-xs font-normal text-gray-700 hover:text-gray-500" href="mailto:{{ payment.user.email }}">
                {{ payment.user.email }}
              </a>
              <ng-container *vsLet="payment.paymentMethod?.billingAddress as address">
                <p class="text-xs font-normal text-gray-700 whitespace-normal" *ngIf="address">
                  {{ address.line1 }}<ng-container *ngIf="address.line2">, {{ address.line2 }} </ng-container><br />
                  {{ address.city }} {{ address.state }} {{ address.zip }}<br />
                  {{ address.country }}
                </p>
              </ng-container>
            </div>
          </div>

          <!-- Ship to -->
          <div *ngIf="!isSameAddress" class="flex flex-col gap-2 flex-1">
            <div class="text-xs font-semibold text-gray-900">Ship to</div>
            <div class="flex flex-col">
              <p class="text-sm font-semibold text-gray-900" *ngIf="payment.shipToName">
                {{ payment.shipToName }}
              </p>
              <a
                class="text-xs font-normal text-gray-700 hover:text-gray-500"
                href="mailto:{{ payment.shipToEmail }}"
                *ngIf="payment.shipToEmail"
              >
                {{ payment.shipToEmail }}
              </a>
              <ng-container *vsLet="payment.shippingAddress as address">
                <p class="text-xs font-normal text-gray-700 whitespace-normal" *ngIf="address">
                  {{ address.line1 }}<ng-container *ngIf="address.line2">, {{ address.line2 }} </ng-container><br />
                  {{ address.city }} {{ address.state }} {{ address.zip }}<br />
                  {{ address.country }}
                </p>
              </ng-container>
            </div>
          </div>
        </div>
      </div>

      <!-- Metadata -->
      <div class="mb-4 grid grid-cols-2 gap-x-8">
        <div class="flex justify-between gap-2 px-1 py-2 border-y border-gray-200">
          <p class="truncate text-gray-700 text-sm font-semibold">Billed on</p>
          <p class="truncate text-gray-900 text-sm">{{ payment.created | date: 'mediumDate' }}</p>
        </div>

        <div class="flex justify-between gap-2 px-1 py-2 border-y border-gray-200">
          <p class="truncate text-gray-700 text-sm font-semibold">{{ payment.paymentDate ? 'Paid on' : 'Due on' }}</p>
          <p class="truncate text-gray-900 text-sm" *vsLet="payment.paymentDate || payment.dueDate as dt">
            {{ dt ? (dt | date: 'mediumDate') : '-' }}
          </p>
        </div>

        <ng-container *ngFor="let group of payment.propertyBag">
          <div *ngFor="let prop of group" class="flex justify-between gap-2 px-1 py-2 border-b border-gray-200">
            <p class="truncate text-gray-700 text-sm font-semibold">{{ prop.label }}</p>
            <p class="truncate text-gray-900 text-sm">{{ prop.value }}</p>
          </div>
        </ng-container>
      </div>

      <!-- Internal support metadata. Explicitly excluded from customer views and static receipt output. -->
      <div
        *ngIf="showInternalGatewayAssignment"
        class="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3"
        data-testid="gateway-assignment"
      >
        <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-700">
          {{ payment.paymentDate ? 'Original sale gateway' : 'Assigned gateway' }}
        </p>
        <div class="grid gap-2 text-sm sm:grid-cols-2">
          <div *ngIf="gatewayNamePresentation as gatewayName">
            <p class="text-xs font-semibold text-gray-700">{{ gatewayName.label }}</p>
            <p class="break-words text-gray-900">{{ gatewayName.name }}</p>
            <p *ngIf="gatewayName.note" class="text-xs text-gray-600">
              {{ gatewayName.note }}
            </p>
          </div>
          <div>
            <p class="text-xs font-semibold text-gray-700">Provider</p>
            <p class="text-gray-900">{{ paymentGatewayProvider(payment.gateway) || 'Not recorded' }}</p>
          </div>
          <div *ngIf="gatewayRoutingReasonLabel(payment.gatewayRoutingReason) as routingReason">
            <p class="text-xs font-semibold text-gray-700">Gateway routing reason</p>
            <p class="text-gray-900">{{ routingReason }}</p>
          </div>
        </div>
      </div>

      <div
        *ngIf="payment.collectionMode === 'OUT_OF_BAND' || payment.collectionMode === 'PAY_LATER'"
        class="mb-4 rounded-xl border border-gray-200 p-4 space-y-2"
      >
        <p class="font-semibold">
          {{ collectionLabel(payment) }}
        </p>
        <p *ngIf="static || (currentStaff$ | async)" class="text-sm">
          {{ payment.collectionMode === 'PAY_LATER' ? 'Responsible payer' : 'Seller owner' }}:
          {{ payment.user.displayName || payment.user.email }}
        </p>
        <p class="text-sm">
          {{ payment.collectionMode === 'PAY_LATER' ? 'Balance' : 'Installment' }}:
          {{ payment.amount / 100 | currency }}
          <span *ngIf="payment.dueDate; else noReceiptDueDate"> · Due {{ payment.dueDate | date: 'mediumDate' }}</span>
          <ng-template #noReceiptDueDate> · No due date assigned</ng-template>
        </p>
        <div *ngIf="payment.externalSettlement as receipt" class="text-sm space-y-1">
          <p>
            Received {{ receipt.receivedDate | date: 'mediumDate' : 'UTC' }} · {{ receipt.channel | titlecase }} ·
            {{ receipt.currency }}
          </p>
          <p>
            Recorded by {{ receipt.actorName }} on
            {{ receipt.recordedAt | date: 'medium' }}
          </p>
          <p *ngIf="receipt.reference">Reference: {{ receipt.reference }}</p>
          <p *ngIf="receipt.note">Note: {{ receipt.note }}</p>
          <button
            *ngIf="!static && receipt.attachment && (currentStaff$ | async)"
            vs-button
            size="sm"
            appearance="outline"
            (click)="downloadReceiptAttachment()"
          >
            Proof of receipt: {{ receipt.attachment.filename }}
          </button>
        </div>
        <ng-container *ngIf="payment.collectionMode === 'PAY_LATER' && !payment.paymentDate && !static">
          <p *ngIf="currentStaff$ | async; else customerPayLaterNote" class="text-sm text-gray-600">
            The warranty stays a draft until this balance is settled. Once it is, the warranty is registered
            automatically, and activated if its policy doesn't require a separate activation.
          </p>
          <ng-template #customerPayLaterNote>
            <p class="text-sm text-gray-600">Your warranty is pending until this balance is paid.</p>
          </ng-template>
        </ng-container>
        <div *ngIf="!static && !showReceiptForm" class="flex flex-wrap gap-2">
          <button
            *ngIf="canCollectPayLater$ | async"
            vs-button
            size="sm"
            theme="primary"
            (click)="openPayLater(retryPaymentDialog, payLaterPlanDialog)"
          >
            {{ (currentStaff$ | async) ? 'Collect payment' : 'Pay now' }}
          </button>
          <button
            *ngIf="canSettleExternal$ | async"
            vs-button
            size="sm"
            theme="primary"
            [appearance]="(canCollectPayLater$ | async) ? 'outline' : 'default'"
            (click)="showReceiptForm = true"
          >
            Record external receipt
          </button>
        </div>
        <ng-container *ngIf="!static && (canSettleExternal$ | async)">
          <form
            *ngIf="showReceiptForm"
            [formGroup]="receiptForm"
            (ngSubmit)="recordExternalReceipt()"
            class="grid gap-3 pt-2"
          >
            <p class="text-sm font-medium">
              Full amount received: {{ payment.amount / 100 | number: '1.2-2' }}. Recording this receipt settles this
              {{ payment.collectionMode === 'PAY_LATER' ? 'balance' : 'installment' }} in full.
            </p>
            <label class="text-sm"
              >Currency
              <select formControlName="currency" class="block w-full border rounded p-2">
                <option value="USD">USD</option>
                <option value="CAD">CAD</option>
              </select>
            </label>
            <label class="text-sm"
              >Receipt date <input type="date" formControlName="receivedDate" class="block w-full border rounded p-2"
            /></label>
            <label class="text-sm"
              >External payment channel
              <select formControlName="channel" class="block w-full border rounded p-2">
                <option *ngFor="let channel of externalChannels" [value]="channel">{{ channel | titlecase }}</option>
              </select>
            </label>
            <label class="text-sm"
              >External reference (optional)
              <input type="text" formControlName="reference" maxlength="255" class="block w-full border rounded p-2"
            /></label>
            <label class="text-sm"
              >Note (optional)
              <textarea formControlName="note" maxlength="2000" class="block w-full border rounded p-2"></textarea>
            </label>
            <div class="flex gap-2">
              <label class="text-sm"
                >Proof of receipt (optional)
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  [disabled]="settling"
                  (change)="selectReceiptAttachment($event)"
                  class="block w-full border rounded p-2"
                />
                <span class="text-xs text-gray-600">PDF or image, up to 10 MB.</span>
              </label>
            </div>
            <div class="flex gap-2">
              <button vs-button type="submit" theme="primary" [disabled]="receiptForm.invalid || settling">
                {{ settling ? 'Recording…' : 'Record full receipt' }}
              </button>
              <button
                vs-button
                type="button"
                appearance="outline"
                [disabled]="settling"
                (click)="showReceiptForm = false"
              >
                Cancel
              </button>
            </div>
          </form>
        </ng-container>
      </div>

      <!-- Payment failure alert -->
      <div
        *ngIf="!static && payment.status === 'FAILED' && payment.collectionMode !== 'OUT_OF_BAND'"
        class="mb-4 rounded-xl border border-danger-300 relative overflow-hidden px-3 py-2"
      >
        <div class="bg-danger-50 opacity-50 absolute w-full h-full top-0 left-0 -z-10"></div>
        <div class="grid grid-cols-[auto_1fr] gap-3">
          <div
            class="w-10 min-w-[40px] h-10 flex items-center justify-center bg-base rounded-lg border border-danger-200"
          >
            <vs-icon name="alert-circle" size="20" class="text-danger-700"></vs-icon>
          </div>
          <div>
            <p class="font-semibold text-danger-700">Payment failed</p>
            <p class="text-sm font-normal text-danger-700">
              {{ payment.details || 'An error occurred while processing this payment.' }}
            </p>
          </div>
        </div>
      </div>

      <!-- Details -->
      <div class="flex flex-wrap-reverse gap-4 items-center justify-between">
        <div class="flex-col">
          <div class="font-semibold text-gray-900">{{ payment.title }}</div>
          <div class="text-gray-700 text-sm">{{ payment.referenceId }}</div>
        </div>

        <div *ngIf="!static" class="flex flex-wrap items-center gap-2">
          <!-- Resend Reminder Email -->
          <button
            *ngIf="
              payment.status === 'FAILED' &&
              payment.collectionMode === 'GATEWAY' &&
              !hideResendReminderButton &&
              (canRetryBillingInfo$ | async)
            "
            vs-button
            size="sm"
            class="flex-1"
            appearance="outline"
            (click)="resendPaymentReminderDialog.open()"
          >
            Resend payment reminder
          </button>

          <!-- Retry Payment -->
          <button
            *ngIf="
              payment.status === 'FAILED' && payment.collectionMode === 'GATEWAY' && (canRetryBillingInfo$ | async)
            "
            vs-button
            size="sm"
            class="flex-1"
            theme="primary"
            (click)="retryPaymentDialog.open()"
          >
            Retry payment
          </button>
        </div>
      </div>

      <!-- Pricing breakdown -->
      <div class="grid grid-cols-[1fr_auto_auto] gap-x-20 gap-y-2 text-sm">
        <vs-divider class="col-span-3"></vs-divider>
        <ng-container *ngFor="let breakdown of payment.priceBreakdown; let last = last">
          <div class="col-span-2 truncate">
            <span class="font-semibold"> {{ breakdown.title }}{{ breakdown.subtitle ? ' - ' : '' }} </span>
            {{ breakdown.subtitle }}
          </div>
          <div class="text-right">{{ breakdown.amount / 100 | currency }}</div>
          <vs-divider class="col-span-3"></vs-divider>
        </ng-container>

        <div class="col-start-2 font-semibold text-gray-700 truncate">Subtotal</div>
        <div class="text-gray-900 text-right font-semibold">{{ payment.subtotal / 100 | currency }}</div>
        <vs-divider class="col-start-2 col-span-2"></vs-divider>

        <ng-container *ngFor="let tax of payment.taxes; let last = last">
          <div class="col-start-2 font-medium text-gray-700 truncate">{{ tax.label }} {{ tax.rate * 100 }}%</div>
          <div class="text-gray-900 text-right">{{ tax.amount / 100 | currency }}</div>
          <vs-divider class="col-span-3"></vs-divider>
        </ng-container>

        <div class="col-start-2 font-semibold text-gray-700 truncate">Total</div>
        <div class="text-gray-900 text-right font-semibold">{{ payment.amount / 100 | currency }}</div>

        <vs-divider class="col-start-2 col-span-2"></vs-divider>

        <ng-container *ngIf="(static && payment.paymentMethod) || (!static && (canViewPaymentMethods$ | async))">
          <div class="col-start-2 font-semibold text-gray-700 truncate flex items-center">Payment Method</div>
          <div class="text-gray-900 text-right font-semibold">
            <span class="text-gray-700 whitespace-nowrap text-sm font-medium"></span>
            <span
              *ngIf="payment.paymentMethod; else noPaymentMethod"
              class="text-gray-900 whitespace-nowrap text-sm font-normal text-center"
            >
              <vs-payment-method-item [paymentMethod]="payment.paymentMethod" [minify]="true"></vs-payment-method-item>
            </span>
            <ng-template #noPaymentMethod>
              <span class="text-gray-700 whitespace-nowrap text-sm font-normal">No payment details on file</span>
            </ng-template>
          </div>
        </ng-container>
      </div>
    </div>

    <!-- footer (brand and contact info) -->
    <div *ngIf="static" class="absolute bottom-0 flex items-center gap-2">
      <p class="text-sm font-semibold text-gray-900">
        {{ brandName }}
      </p>
      -
      <a class="text-xs font-normal text-gray-600 hover:text-gray-500" href="mailto:{{ contactEmail }}">
        {{ contactEmail }}
      </a>
    </div>
  </div>

  <vs-retry-payment-dialog
    #retryPaymentDialog
    [payment]="payment"
    [labels]="
      payment.collectionMode === 'PAY_LATER'
        ? {
            title: 'Pay balance',
            subtitle: 'Choose a payment method to pay this balance in full.',
            action: 'Pay balance',
            success: 'The balance has been paid.',
          }
        : undefined
    "
    (saved)="paymentRetried($event)"
    (failed)="paymentAttemptFailed()"
  ></vs-retry-payment-dialog>

  <!-- VS-1458: pay a Pay Later balance on one of its term's payment schedules -->
  <vs-dialog #payLaterPlanDialog>
    <vs-dialog-header
      class="block mb-4"
      [config]="{
        mode: 'simple',
        showDivider: false,
        iconTheme: 'primary',
        icon: 'calendar',
        title: 'Choose how to pay',
        subtitle: 'Pay this balance in full, or on one of the installment plans its term offers.',
      }"
    ></vs-dialog-header>

    <div class="flex flex-col gap-2">
      <button
        *ngFor="let option of payLaterPlans"
        type="button"
        class="flex items-center justify-between gap-3 rounded border p-3 text-left"
        [class.border-primary-500]="selectedPlan?.scheduleId === option.scheduleId"
        (click)="choosePayLaterPlan(option, retryPaymentDialog, payLaterPlanDialog)"
      >
        <span class="text-sm font-semibold">{{ option.title }}</span>
        <span class="text-sm text-gray-600 text-right">
          <ng-container *ngIf="option.installments > 1; else inFull">
            {{ option.installments }} × {{ option.installmentAmount / 100 | currency }}
            <span class="block text-xs">{{ option.total / 100 | currency }} in total</span>
          </ng-container>
          <ng-template #inFull>{{ option.total / 100 | currency }}</ng-template>
        </span>
      </button>
    </div>

    <div *ngIf="selectedPlan" class="flex flex-col gap-3 pt-4">
      <p class="text-sm text-gray-600">
        The first installment of {{ selectedPlan.installmentAmount / 100 | currency }} is due today. The other
        {{ selectedPlan.installments - 1 }} are charged automatically every
        {{ selectedPlan.intervalDays | number: '1.0-0' }} days to the card below. The warranty is registered once the
        first installment is paid.
      </p>

      <vs-select-or-create-payment-method
        [address]="payment.shippingAddress"
        [gatewayInstanceId]="payment.gatewayInstanceId"
        [userId]="payment.userId"
        [formControl]="planPaymentMethod"
      ></vs-select-or-create-payment-method>

      <label *ngIf="canSettleExternal$ | async" class="flex items-center gap-2 text-sm">
        <input type="checkbox" [checked]="planFirstByReceipt" (change)="planFirstByReceipt = !planFirstByReceipt" />
        Record an external receipt for the first installment instead of charging it
      </label>

      <form *ngIf="planFirstByReceipt" [formGroup]="receiptForm" class="grid gap-3">
        <p class="text-sm font-medium">Amount received: {{ selectedPlan.installmentAmount / 100 | currency }}</p>
        <label class="text-sm"
          >Currency
          <select formControlName="currency" class="block w-full border rounded p-2">
            <option value="USD">USD</option>
            <option value="CAD">CAD</option>
          </select>
        </label>
        <label class="text-sm"
          >Receipt date <input type="date" formControlName="receivedDate" class="block w-full border rounded p-2"
        /></label>
        <label class="text-sm"
          >External payment channel
          <select formControlName="channel" class="block w-full border rounded p-2">
            <option *ngFor="let channel of externalChannels" [value]="channel">{{ channel | titlecase }}</option>
          </select>
        </label>
        <label class="text-sm"
          >External reference (optional)
          <input type="text" formControlName="reference" maxlength="255" class="block w-full border rounded p-2"
        /></label>
      </form>

      <div class="flex items-center gap-2">
        <button
          vs-button
          class="flex-1"
          appearance="outline"
          [disabled]="payingPlan"
          (click)="payLaterPlanDialog.close()"
        >
          Cancel
        </button>
        <button
          vs-button
          class="flex-1"
          theme="primary"
          [disabled]="payingPlan || planPaymentMethod.invalid || (planFirstByReceipt && receiptForm.invalid)"
          (click)="payOnPlan(payLaterPlanDialog)"
        >
          {{
            payingPlan ? 'Processing…' : planFirstByReceipt ? 'Record receipt and start plan' : 'Pay first installment'
          }}
        </button>
      </div>
    </div>
  </vs-dialog>

  <vs-dialog #resendPaymentReminderDialog (closed)="$event && resendPaymentReminder()">
    <vs-dialog-header
      class="block mb-6"
      [config]="{
        mode: 'simple',
        showDivider: false,
        hideCloseButton: true,

        iconTheme: 'primary',
        icon: 'alert-circle',

        title: 'Resend payment reminder',
        subtitle: 'Send a reminder to complete payment to ' + payment.user.displayName + ' at ' + payment.user.email,
      }"
    ></vs-dialog-header>

    <div class="flex items-center gap-2">
      <button vs-button class="flex-1" appearance="outline" (click)="resendPaymentReminderDialog.close(false)">
        Cancel
      </button>
      <button vs-button class="flex-1" theme="primary" (click)="resendPaymentReminderDialog.close(true)">
        Confirm
      </button>
    </div>
  </vs-dialog>
</ng-container>
